import { ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { HEADER_HEIGHT, Z_INDEX } from '@/config/constants';
import { AdminSearchResultsPanel } from '@/components/Admin/AdminWorkspace/AdminSearchResultsPanel';
import { InviteUserModal } from '@/components/ConfirmModals/InviteUserModal';

/** Pages under a nav item (e.g. /admin/user/:id) keep it highlighted. */
const ALSO_ACTIVE: Record<string, string[]> = {
  '/admin/user': ['/admin/user/'],
  '/admin/product': ['/admin/product/'],
  '/admin/properties': ['/admin/property/'],
  '/admin/loan-pipeline': ['/admin/loan-pipeline/'],
  '/admin/messenger': ['/admin/feedback'],
  '/admin/email': ['/admin/email/'],
};

/** Search people, properties, contracts and loan files from any admin page. */
const HeaderSearch = () => {
  const theme = useTheme();
  const location = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState('');
  useEffect(() => setAnchor(null), [location.pathname]);
  return (
    <>
      <Tooltip title="Search everything" arrow>
        <IconButton
          aria-label="Search everything"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: theme.palette.greyShades.grey1 }}
        >
          <i className="fa-light fa-magnifying-glass" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { width: 'min(440px, calc(100vw - 32px))', p: 1.5 } },
        }}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Name, email, phone, address or ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i className="fa-light fa-magnifying-glass" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: q.trim().length >= 2 ? 1 : 0 }}
        />
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <AdminSearchResultsPanel
            query={q}
            dense
            onNavigate={() => setAnchor(null)}
          />
        </Box>
      </Popover>
    </>
  );
};

const NavIconButton = ({
  url,
  label,
  short,
  children,
}: {
  url: string;
  label: string;
  /** Visible caption next to the icon from tablet width up; matches the page title. */
  short?: string;
  children: ReactNode;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive =
    location.pathname === url ||
    (ALSO_ACTIVE[url] ?? []).some((p) => location.pathname.startsWith(p));

  return (
    <Tooltip title={label} arrow>
      <IconButton
        onClick={() => navigate(url)}
        aria-label={label}
        sx={{
          color: isActive
            ? theme.palette.mainColors.primary
            : theme.palette.greyShades.grey1,
          position: 'relative',
          flexDirection: 'column',
          gap: '2px',
          borderRadius: 1.5,
          px: { md: 0.75 },
          flexShrink: 0,
          '&::after': {
            content: isActive ? '""' : 'none',
            position: 'absolute',
            left: 0,
            bottom: '0px',
            width: '100%',
            height: '2px',
            backgroundColor: theme.palette.mainColors.primary,
          },
        }}
      >
        {children}
        {short && (
          <Typography
            component="span"
            sx={{
              display: { xs: 'none', md: 'block' },
              fontSize: '10px',
              lineHeight: 1.1,
              fontWeight: isActive ? 700 : 500,
              whiteSpace: 'nowrap',
              color: 'inherit',
            }}
          >
            {short}
          </Typography>
        )}
      </IconButton>
    </Tooltip>
  );
};

export const AdminHeader = () => {
  const theme = useTheme();
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: 'var(--header-glass)',
        backdropFilter: 'blur(14px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.2)',
        borderBottom:
          '1px solid color-mix(in srgb, var(--outline-variant) 50%, transparent)',
        boxShadow: '0 8px 28px rgba(0, 8, 30, 0.06)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z_INDEX.HEADER,
      }}
    >
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          height: `${HEADER_HEIGHT}px`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '12px', md: '24px' },
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="h3"
            className="font-display"
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--primary-navy)',
            }}
          >
            ADMIN
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '8px', md: '2px' },
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <NavIconButton
              url="/admin/user"
              label="Users — everyone, by pipeline stage"
              short="Users"
            >
              <i className="fa-light fa-users" />
            </NavIconButton>
            <NavIconButton
              url="/admin/product"
              label="Estimates"
              short="Estimates"
            >
              <i className="fa-light fa-house-building" />
            </NavIconButton>
            <NavIconButton
              url="/admin/documents"
              label="Documents"
              short="Documents"
            >
              <i className="fa-light fa-file-contract" />
            </NavIconButton>
            <NavIconButton
              url="/admin/properties"
              label="Contracts"
              short="Contracts"
            >
              <i className="fa-light fa-building-columns" />
            </NavIconButton>
            <NavIconButton
              url="/admin/loan-pipeline"
              label="Loan Files"
              short="Loan Files"
            >
              <i className="fa-light fa-file-invoice-dollar" />
            </NavIconButton>
            <NavIconButton
              url="/admin/loan-checklist-matrices"
              label="Checklists — documents each loan type needs"
              short="Checklists"
            >
              <i className="fa-light fa-list-check" />
            </NavIconButton>
            <NavIconButton
              url="/admin/clauses"
              label="Clauses — FL/VA clause dictionary"
              short="Clauses"
            >
              <i className="fa-light fa-scroll" />
            </NavIconButton>
            <NavIconButton
              url="/admin/payments"
              label="Payments — investor subscriptions"
              short="Payments"
            >
              <i className="fa-light fa-credit-card" />
            </NavIconButton>
            <NavIconButton
              url="/admin/agent"
              label="Agents — assign agents to contracts"
              short="Agents"
            >
              <i className="fa-light fa-people-arrows" />
            </NavIconButton>
            <NavIconButton
              url="/admin/messenger"
              label="Messenger"
              short="Messenger"
            >
              <i className="fa-light fa-comments" />
            </NavIconButton>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <HeaderSearch />
          <Tooltip title="Invite User" arrow>
            <IconButton
              onClick={() => setIsInviteOpen(true)}
              aria-label="Invite User"
              sx={{ color: theme.palette.greyShades.grey1 }}
            >
              <i className="fa-light fa-plus" />
            </IconButton>
          </Tooltip>
          <NavIconButton
            url="/admin/interest-rates"
            label="Rates"
            short="Rates"
          >
            <i className="fa-light fa-percentage" />
          </NavIconButton>
          <NavIconButton url="/admin/email" label="Emails" short="Emails">
            <i className="fa-light fa-envelopes" />
          </NavIconButton>
          <NavIconButton url="/admin" label="Admin Management" short="Admin">
            <i className="fa-light fa-shield-halved" />
          </NavIconButton>
          <NavIconButton url="/main" label="Back to Main" short="Exit">
            <i className="fa-light fa-turn-right" />
          </NavIconButton>
        </Box>
      </Container>
      <InviteUserModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
      />
    </Box>
  );
};
